import { CaretDownIcon, CheckIcon, GlobeIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { parsePhoneNumber } from "react-phone-number-input";

import { CV_COUNTRIES, type CvCountry, findCountryByCode } from "./cv-countries";

type Props = {
  value: string;
  preferredCountry?: CvCountry;
  onChange: (value: string) => void;
};

// Le pays préféré l'emporte sur un indicatif partagé (+1, +44…), puis l'analyse du numéro.
export const detectPhoneCountry = (phone: string, preferredCountry?: CvCountry) => {
  const trimmed = phone.trim();

  if (!trimmed.startsWith("+")) return undefined;
  if (preferredCountry && trimmed.startsWith(preferredCountry.dialCode)) return preferredCountry;

  return (
    findCountryByCode(parsePhoneNumber(trimmed)?.country) ??
    CV_COUNTRIES.filter((country) => trimmed.startsWith(country.dialCode)).sort(
      (a, b) => b.dialCode.length - a.dialCode.length,
    )[0]
  );
};

export const PhoneInput = ({ value, preferredCountry, onChange }: Props) => {
  const [country, setCountry] = useState<CvCountry | undefined>(
    () => detectPhoneCountry(value, preferredCountry) ?? preferredCountry,
  );
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const localNumber = country ? value.replace(country.dialCode, "").trimStart() : value;
  const withDialCode = (number: string) =>
    country ? `${country.dialCode}${number ? ` ${number}` : ""}` : number;

  useEffect(() => {
    if (preferredCountry && !value.trim()) setCountry(preferredCountry);
  }, [preferredCountry, value]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const countries = useMemo(() => {
    const normalized = query.toLowerCase().trim();
    if (!normalized) return CV_COUNTRIES;
    return CV_COUNTRIES.filter((item) =>
      `${item.name} ${item.dialCode}`.toLowerCase().includes(normalized),
    );
  }, [query]);

  const chooseCountry = (nextCountry: CvCountry) => {
    setCountry(nextCountry);
    setOpen(false);
    setQuery("");
    onChange(`${nextCountry.dialCode}${localNumber ? ` ${localNumber}` : ""}`);
  };

  return (
    <div
      ref={rootRef}
      className="relative flex min-h-10 rounded-xl border border-slate-200 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100"
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Choisir l’indicatif téléphonique"
        className="flex shrink-0 items-center gap-1.5 rounded-l-xl border-r border-slate-200 px-2.5 text-xs font-semibold text-indigo-950 hover:bg-indigo-50"
      >
        {country ? (
          <>
            <span className="text-base leading-none">{country.flag}</span>
            <span>{country.dialCode}</span>
          </>
        ) : (
          <>
            <GlobeIcon size={16} className="text-slate-400" />
            <span>Indicatif</span>
          </>
        )}
        <CaretDownIcon
          size={12}
          className={cn("text-slate-400 transition-transform", open && "rotate-180")}
        />
      </button>
      <input
        value={localNumber}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="Numéro sans l’indicatif"
        onChange={(event) => onChange(withDialCode(event.target.value))}
        className="min-w-0 flex-1 rounded-r-xl bg-transparent px-3 text-xs text-indigo-950 outline-none"
      />

      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-[min(330px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-indigo-100 bg-white shadow-[0_16px_40px_rgba(30,27,75,0.15)]">
          <div className="relative border-b border-slate-100 p-2">
            <MagnifyingGlassIcon
              size={14}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Pays ou indicatif"
              className="min-h-9 w-full rounded-lg bg-slate-50 pl-9 pr-3 text-xs outline-none focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1.5 [scrollbar-color:rgb(165_180_252)_transparent] [scrollbar-width:thin]">
            {countries.map((item) => (
              <button
                key={item.code}
                type="button"
                onClick={() => chooseCountry(item)}
                className="flex min-h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs text-indigo-950 hover:bg-indigo-50"
              >
                <span className="text-base">{item.flag}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{item.name}</span>
                <span className="text-slate-500">{item.dialCode}</span>
                {country?.code === item.code && (
                  <CheckIcon size={13} weight="bold" className="text-emerald-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
